import React from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import ToolbarButton from '../../common/ToolbarButton';

// Shared look for the inline feedback alerts: hug the content instead of
// stretching across the card, and keep the icon, text, and close button on
// one centered line.
const feedbackAlertSx = {
  mt: 1,
  width: 'fit-content',
  maxWidth: '100%',
  alignItems: 'center',
  borderRadius: 2,
  py: 0.25,
  '& .MuiAlert-icon': { py: 0.5, alignItems: 'center' },
  '& .MuiAlert-message': { py: 0.5 },
  '& .MuiAlert-action': { pt: 0, ml: 1, alignItems: 'center' }
};

const UncategorizedProgressCard = ({
  uncategorizedCount,
  totalTransactions,
  categorizeSuccess,
  categorizeMessage,
  applyingRules,
  aiCategorizing,
  onCategorizeAll,
  onAiCategorize,
  onCreateRule
}) => {
  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        display: 'flex',
        // Three action buttons don't fit beside the progress info until md,
        // so keep the stacked layout through the sm range.
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        justifyContent: 'space-between',
        minHeight: { xs: 100, sm: 120 },
        mb: { xs: 2, sm: 4 },
        width: '100%',
        maxWidth: '100%',
        boxShadow: '0 4px 20px rgba(255, 193, 7, 0.08)',
         bgcolor: uncategorizedCount === 0 && categorizeSuccess ? '#fff' : '#fff8e1', // white if none, yellow otherwise
        transition: 'background 0.5s',
        gap: { xs: 2, md: 0 },
      }}
    >
      {/* Progress Circle and Info */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          flex: 1,
          minWidth: 0,
          flexDirection: { xs: 'row', sm: 'row' },
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex', mr: { xs: 1, sm: 2 } }}>
          <CircularProgress
            variant="determinate"
            value={
              uncategorizedCount === 0
                ? 100
                : Math.min(100, 100 * (1 - uncategorizedCount / (totalTransactions || 1)))
            }
            size={60}
            thickness={5}
            sx={(theme) => ({
              color:
                uncategorizedCount === 0
                  ? theme.palette.primary.main
                  : uncategorizedCount < 5
                  ? theme.palette.warning.main
                  : theme.palette.error.main,
              background: 'rgba(0,0,0,0.02)',
              borderRadius: '50%',
            })}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={(theme) => {
                // Calculate font size based on number of digits to ensure it fits in the circle
                const numDigits = uncategorizedCount.toString().length;
                let baseFontSize;
                
                if (numDigits <= 1) {
                  baseFontSize = { xs: '1.4rem', sm: '1.6rem' };
                } else if (numDigits <= 2) {
                  baseFontSize = { xs: '1.2rem', sm: '1.4rem' };
                } else if (numDigits <= 3) {
                  baseFontSize = { xs: '1rem', sm: '1.2rem' };
                } else if (numDigits <= 4) {
                  baseFontSize = { xs: '0.875rem', sm: '1rem' };
                } else {
                  baseFontSize = { xs: '0.75rem', sm: '0.875rem' };
                }
                
                return {
                  color:
                    uncategorizedCount === 0
                      ? theme.palette.primary.main
                      : uncategorizedCount < 5
                      ? theme.palette.warning.dark
                      : theme.palette.error.main,
                  fontSize: baseFontSize,
                  lineHeight: 1,
                  textAlign: 'center',
                };
              }}
            >
              {uncategorizedCount}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={(theme) => ({
              color:
                uncategorizedCount === 0
                  ? theme.palette.primary.main
                  : uncategorizedCount < 5
                  ? theme.palette.warning.dark
                  : theme.palette.error.main,
              fontSize: { xs: '1rem', sm: '1.15rem' },
            })}
          >
            Uncategorized Transactions
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({
              color: uncategorizedCount === 0 ? theme.palette.primary.dark : theme.palette.warning.dark,
              fontSize: { xs: '0.9rem', sm: '1rem' },
            })}
          >
            {uncategorizedCount === 0
              ? categorizeSuccess
                ? 'All transactions have been categorized!'
                : 'All caught up! ✅'
              : 'Ready to be sorted and categorized.'}
          </Typography>
          {/* Feedback message */}
          {categorizeMessage && (
            <Alert
              severity={categorizeSuccess ? 'success' : 'error'}
              sx={{
                ...feedbackAlertSx,
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              {categorizeMessage}
            </Alert>
          )}
        </Box>
      </Box>
      
      {/* Action Buttons — same ToolbarButton as the other list-page toolbars.
          On xs the three buttons share one row equally instead of stacking. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          gap: { xs: 1, sm: 1.5, md: 2 },
          alignItems: 'center',
          width: { xs: '100%', md: 'auto' },
          justifyContent: { xs: 'stretch', sm: 'center', md: 'flex-end' },
        }}
      >
        <ToolbarButton
          startIcon={applyingRules ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon fontSize="small" />}
          onClick={onCategorizeAll}
          disabled={applyingRules || uncategorizedCount === 0}
          sx={{ flex: { xs: 1, sm: '0 0 auto' }, minWidth: { xs: 0, sm: 110, md: 120 } }}
        >
          {applyingRules ? 'Categorizing...' : 'Run All'}
        </ToolbarButton>

        <Tooltip title="Uses AI to categorize all uncategorized transactions. To scope it, select transactions below and use the AI button in the selection bar.">
          {/* Real box (not display:contents) so the Tooltip can anchor to it,
              and hover still works while the button is disabled. */}
          <Box
            component="span"
            sx={{ display: 'inline-flex', flex: { xs: 1, sm: '0 0 auto' }, minWidth: { xs: 0, sm: 110, md: 120 } }}
          >
            <ToolbarButton
              color="secondary"
              startIcon={aiCategorizing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
              onClick={onAiCategorize}
              disabled={applyingRules || aiCategorizing || uncategorizedCount === 0}
              sx={{ width: '100%', minWidth: { xs: 0, sm: 110, md: 120 } }}
            >
              {aiCategorizing ? 'Thinking...' : 'AI Categorize'}
            </ToolbarButton>
          </Box>
        </Tooltip>

        <ToolbarButton
          startIcon={<AddIcon fontSize="small" />}
          onClick={onCreateRule}
          sx={{ flex: { xs: 1, sm: '0 0 auto' }, minWidth: { xs: 0, sm: 110, md: 120 } }}
        >
          Add
        </ToolbarButton>
      </Box>
    </Paper>
  );
};

export default UncategorizedProgressCard;